/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import React, { useState } from "react";
import LegalbirdLoader from "@/src/components/ContentLoader/LegalbirdLoader";
import { Navigate } from "react-router";
import { STATUS_EXTRAJUDICIAL_REACTION_RECEIVED } from "@/src/services/litigationCase/leadStatus";
import { getCaseLink, getProductData, getProductNameBy } from "@/src/services/productService";
import _cloneDeep from "lodash/cloneDeep";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchResource, queryKeys, updateResource } from "@/src/services/reactQuery/reactQueryService";
import { useInquiryToCustomers } from "@/src/hooks/queries/useInquiryToCustomers";
import useProductParams from "@/src/hooks/useProductParams";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import { InquiryToCustomerStatus } from "@/src/types/Entities/InquiryToCustomer";
import TrafficAccidentFeedbackForm from "@/src/components/Form/LitigationCase/FeedbackForm/TrafficAccidentFeedbackForm";
import LayoutProtectedRoutes from "@/src/containers/Layouts/LayoutProtectedRoutes";
import ProductFeedbackPage from "@/src/components/Serviceworld/Feedback/ProductFeedbackPage";
import FeedbackConfirmationPage from "@/src/components/Serviceworld/Feedback/FeedbackConfirmationPage";

const FeedbackPage = () => {
  const { productName, productId } = useProductParams();
  const productClassName = getProductNameBy("urlPartShort", productName);
  const productApiUri = getProductData(productClassName, "apiUri");
  const { data: product, isLoading } = useQuery({
    queryKey: queryKeys.item(productApiUri, productId),
    queryFn: () => fetchResource(productApiUri, productId),
  });
  const [isLoadingInternal, setIsLoadingInternal] = useState(false);
  const queryClient = useQueryClient();
  const updateMutation = useMutation({
    mutationFn: updateResource,
    onSuccess: (data, variables) => queryClient.setQueryData(queryKeys.item(variables.uri, variables.id), data),
  });

  const inquiryFilters = {
    identifier: "customerFeedbackInquiry",
    productId: productId,
    "status[0]": InquiryToCustomerStatus.STATUS_OPEN,
    "status[1]": InquiryToCustomerStatus.STATUS_OVERDUE,
    "status[2]": InquiryToCustomerStatus.STATUS_IN_PROGRESS,
  };
  const { data: inquiries } = useInquiryToCustomers(inquiryFilters, !!product);

  if (isLoading || !product || !inquiries) {
    return (
      <LayoutProtectedRoutes>
        <LegalbirdLoader />
      </LayoutProtectedRoutes>
    );
  }

  if (!!product.finalClaimStatusClient || product.leadStatus !== STATUS_EXTRAJUDICIAL_REACTION_RECEIVED) {
    return <FeedbackConfirmationPage caseLink={getCaseLink(product)} />;
  }

  const currentInquiryToCustomer = inquiries[0];

  if (!currentInquiryToCustomer || currentInquiryToCustomer.identifier !== "customerFeedbackInquiry") {
    return <Navigate to={getCaseLink(product)} />;
  }

  const getHintText = (product: AbstractCase) => {
    switch (product.extrajudicialReaction) {
      case "noReaction":
        return (
          <>
            <strong>Die Haftpflichtversicherung des Unfallgegners hat leider nicht auf unser Schreiben reagiert.</strong>
            <br />
            Bitte geben Sie uns hier eine Rückmeldung darüber, ob Sie zwischenzeitlich etwas von der Haftpflichtversicherung des Unfallgegners gehört haben und
            wie weiter vorgegangen werden soll.
          </>
        );
      case "fullyAgreed":
        return (
          <>
            <strong>
              Wir haben gute Nachrichten für Sie! Die Haftpflichtversicherung Ihres Unfallgegners hat uns mitgeteilt, dass sie Ihren Schaden vollständig
              regulieren wird.
            </strong>
            <br />
            Bitte geben Sie uns eine Rückmeldung darüber, ob Sie damit einverstanden sind und der Fall somit für Sie abgeschlossen ist.
          </>
        );
      case "partiallyAgreed":
        return (
          <>
            <strong>Die Haftpflichtversicherung Ihres Unfallgegners hat uns mitgeteilt, dass sie Ihren Schaden teilweise regulieren wird. </strong>
            <br />
            Bitte geben Sie uns hier eine Rückmeldung darüber, ob Sie damit einverstanden sind und der Fall für Sie somit abgeschlossen ist.
          </>
        );
      case "disagreed":
        return (
          <>
            <strong>Leider hat uns die Haftpflichtversicherung Ihres Unfallgegners mitgeteilt, dass sie Ihren Schaden nicht regulieren wird.</strong>
            <br />
            Bitte geben Sie uns eine Rückmeldung darüber, wie Sie weiter vorgehen möchten.
          </>
        );
      default:
        return "";
    }
  };

  const initialValues = {
    reactionReceived: null,
    clientAgrees: null,
    finalClaimStatusClient: product.extrajudicialReaction === "fullyAgreed" ? "fullyAgreed" : "notAgreed",
    finalProcessFeedbackClient: null,
    additionalCustomerNote: "",
  };

  const handleSubmit = async ({ values }: { values: AbstractCase }) => {
    setIsLoadingInternal(true);
    const updateData = _cloneDeep(values);

    if (values.clientAgrees === "no" || values.finalProcessFeedbackClient === "lawsuit") {
      updateData.finalClaimStatusClient = "notAgreed";
    } else {
      updateData.finalClaimStatusClient = "fullyAgreed";
    }

    if (updateData.clientAgrees === "yes") {
      updateData.finalProcessFeedbackClient = null;
    }

    if (updateData.additionalCustomerNote === "") {
      updateData.additionalCustomerNote = null;
    }
    await updateMutation.mutateAsync(
      {
        uri: getProductData(product.name, "apiUri"),
        id: product.id,
        data: updateData,
      },
      {
        // This marks the inquiry as done in BE subscriber, so collections should refetch
        onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.collections("/inquiry_to_customers") }),
      },
    );
    setIsLoadingInternal(false);
  };

  return (
    <ProductFeedbackPage hintText={getHintText(product)} initialValues={initialValues} submitAction={handleSubmit}>
      <TrafficAccidentFeedbackForm product={product} isLoading={isLoadingInternal} />
    </ProductFeedbackPage>
  );
};

export default FeedbackPage;
